import { useQuery } from 'react-apollo';
import { useMemo } from 'react';
import uniqBy from 'lodash.uniqby';
import flatten from 'lodash.flatten';

import { AlbumForPrintBackgrounds } from '../graphql/queries/album';
import { AlbumMembershipGroupForPrintBackgrounds } from '../graphql/queries/album-membership-group';
import { PRINT_ASSET_TYPES } from '../../../../../../constants/print-assets';
import { createAlbumMembershipGroupEntity } from '../components/PhotoPurchasingOptions/helpers';

/**
 * A hook that retrieves print backgrounds from an AlbumMembershipGroup or Album.
 *
 * @param {Object} options The options.
 * @param {string} options.albumMembershipGroupId The album membership group id.
 * @param {string} options.albumId The album id.
 * @param {Array} options.jerseyColors The jersey colors to filter by.
 * @param {Boolean} options.isGreenScreenEnabled whether or not the event is GS
 * @returns {Object} An object containing the print backgrounds and loading state.
 */
const useAlbumMembershipGroupPrintBackgrounds = ({
  albumMembershipGroupId,
  albumId,
  jerseyColors = [],
  isGreenScreenEnabled = false,
}) => {
  const {
    data: { albumMembershipGroup } = {},
    loading: loadingAlbumMembershipGroup,
  } = useQuery(AlbumMembershipGroupForPrintBackgrounds, {
    variables: {
      id: albumMembershipGroupId,
    },
    skip: !albumMembershipGroupId || !isGreenScreenEnabled,
    fetchPolicy: 'cache-first',
  });

  const { data: { album } = {}, loading: loadingAlbum } = useQuery(
    AlbumForPrintBackgrounds,
    {
      variables: {
        id: albumId,
      },
      skip: !albumId || !isGreenScreenEnabled,
      fetchPolicy: 'cache-first',
    },
  );

  /**
   * Retrieves the print backgrounds from the AlbumMembershipGroup or Album.
   */
  const printBackgrounds = useMemo(() => {
    const albumMembershipGroupEntity = createAlbumMembershipGroupEntity(
      albumMembershipGroup,
      album,
    );

    if (!albumMembershipGroupEntity) {
      return [];
    }
    const albumFolder =
      albumMembershipGroupEntity?.album_folder ??
      albumMembershipGroupEntity?.album?.root_album_folder;

    if (albumFolder?.print_assets?.records?.length > 0) {
      const assets = (albumFolder?.print_assets?.records ?? [])
        .filter(
          ({ print_asset: printAsset }) =>
            printAsset.type === PRINT_ASSET_TYPES.BACKGROUND,
        )
        .map(albumFolderPrintAsset => albumFolderPrintAsset.print_asset);

      if (jerseyColors.length > 0) {
        return assets.filter(
          ({ jersey_colors: assetColors }) =>
            !assetColors?.records?.length ||
            !!assetColors?.records?.find(({ jersey_color: color }) =>
              jerseyColors.includes(color),
            ),
        );
      }

      return assets;
    }

    // Get print backgrounds from print packages
    const printPackages =
      albumMembershipGroupEntity.print_packages ||
      album?.root_album_folder.album_folder_print_packages?.records ||
      [];

    const packageBackgrounds = flatten(
      printPackages.map(albumPrintPackage => {
        const printPackage = albumPrintPackage?.print_package;

        if (!printPackage) {
          return [];
        }

        return (printPackage?.print_assets?.records ?? [])
          .filter(
            ({ print_asset: printAsset }) =>
              printAsset.type === PRINT_ASSET_TYPES.BACKGROUND,
          )
          .map(printPackagePrintAsset => printPackagePrintAsset.print_asset);
      }),
    );

    // Get print backgrounds from photo pricing
    const photoPricing =
      albumMembershipGroupEntity.photo_pricing || albumFolder?.photo_pricing;

    const photoPricingBackgrounds = photoPricing
      ? (photoPricing?.print_assets?.records ?? [])
          .filter(
            ({ print_asset: printAsset }) =>
              printAsset.type === PRINT_ASSET_TYPES.BACKGROUND,
          )
          .map(printPackagePrintAsset => printPackagePrintAsset.print_asset)
      : [];

    const assets = uniqBy(
      [...packageBackgrounds, ...photoPricingBackgrounds],
      asset => asset.id,
    );

    if (jerseyColors.length > 0) {
      return assets.filter(
        ({ jersey_colors: assetColors }) =>
          !assetColors?.records?.length ||
          !!assetColors?.records?.find(({ jersey_color: color }) =>
            jerseyColors.includes(color),
          ),
      );
    }

    return assets;
  }, [albumMembershipGroup, album, jerseyColors]);

  return {
    loading: loadingAlbumMembershipGroup || loadingAlbum,
    printBackgrounds,
  };
};

export default useAlbumMembershipGroupPrintBackgrounds;
